import * as React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../../popover";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Command as Cmd } from "cmdk";

export type MultiSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type MultiSelectProps = {
  value: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  triggerClassName?: string;
  iconClassName?: string;
  disabled?: boolean;
  maxBadges?: number;
  showClear?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClear?: () => void;
  showExtraCount?: boolean;
  wrapBadges?: boolean;
  onExtraClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
};

export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results.",
  className,
  triggerClassName,
  iconClassName,
  disabled = false,
  maxBadges = 2,
  showClear = true,
  open,
  onOpenChange,
  onClear,
  showExtraCount = true,
  wrapBadges = true,
  onExtraClick,
}: MultiSelectProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = typeof open === "boolean";
  const actualOpen = disabled ? false : (isControlled ? open! : internalOpen);

  const setOpen = (o: boolean) => {
    if (disabled) return;
    if (!isControlled) setInternalOpen(o);
    onOpenChange?.(o);
  };

  const toggle = (val: string) => {
    if (value.includes(val)) onChange(value.filter((v) => v !== val));
    else onChange([...value, val]);
  };

  const clearAll = () => onClear?.();

  const selected = options?.filter((o) => value?.includes(o.value)) || [];
  const extraCount = Math.max(selected.length - maxBadges, 0);

  const TriggerInner = (
    <>
      {selected.length === 0 ? (
        <span
          className={cn(
            "truncate text-gray-500 dark:text-gray-400",
            disabled ? "font-semibold" : "font-normal"
          )}
        >
          {placeholder}
        </span>
      ) : (
        <div
          className={cn(
            "flex gap-1 items-center w-full",
            wrapBadges ? "flex-wrap gap-y-1" : "flex-nowrap overflow-hidden"
          )}
        >
          {selected
            .slice(0, showExtraCount ? maxBadges : selected.length)
            .map((opt) => (
              <Badge
                key={opt.value}
                variant="secondary"
                className="shrink-0 whitespace-nowrap"
              >
                {opt.label}
              </Badge>
            ))}
          {showExtraCount && extraCount > 0 && (
            <Badge
              variant="secondary"
              className={cn(
                "shrink-0 whitespace-nowrap",
                onExtraClick && "cursor-pointer select-none"
              )}
              onPointerDown={(e) => {
                if (onExtraClick) {
                  e.stopPropagation();
                }
              }}
              onClick={(e) => {
                if (onExtraClick) {
                  e.stopPropagation();
                  e.preventDefault();
                  onExtraClick(e);
                }
              }}
            >
              +{extraCount}
            </Badge>
          )}
        </div>
      )}

      {(!selected || selected.length === 0) && (
        <ChevronDown className={cn("absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 shrink-0 text-gray-500 dark:text-gray-400", iconClassName)} />
      )}
    </>
  );

  const triggerBaseClasses = cn(
    "!h-auto py-2 pl-3 pr-10 text-left justify-start",
    "w-full rounded-md",
    "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600",
    "text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700",
    "flex flex-wrap items-start content-start",
    selected.length === 0 && "text-gray-500 dark:text-gray-400",
    triggerClassName
  );

  return (
    <div className={cn("w-full", className)}>
      <Popover open={actualOpen} onOpenChange={setOpen}>
        <div className="relative">
          <PopoverTrigger asChild>
            {disabled ? (
              <div
                role="button"
                aria-disabled="true"
                tabIndex={-1}
                className={cn(
                  triggerBaseClasses,
                  "cursor-not-allowed opacity-50",
                  "dark:text-gray-600 dark:border-gray-700 dark:!bg-gray-800"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{ backgroundColor: "#F3F3F5", borderColor: "#DCDCDC" }}
              >
                {TriggerInner}
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className={cn(triggerBaseClasses, "!whitespace-normal")}
              >
                {TriggerInner}
              </Button>
            )}
          </PopoverTrigger>

          {showClear && selected.length > 0 && !disabled && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearAll();
              }}
              aria-label="Clear"
              title="Clear"
            >
              <X className="h-4 w-4 opacity-60 hover:opacity-100 text-gray-500 dark:text-gray-400" />
            </button>
          )}
        </div>

        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 contain-layout bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
          align="start"
          avoidCollisions={false}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Cmd>
            <Cmd.Input
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 text-sm outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 border-b border-gray-200 dark:border-gray-700"
            />
            <Cmd.List className="max-h-60 overflow-y-auto p-1">
              <Cmd.Empty className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                {emptyMessage}
              </Cmd.Empty>

              <Cmd.Group className="overflow-hidden p-1">
                {options.map((opt) => {
                  const checked = value?.includes(opt.value);
                  return (
                    <Cmd.Item
                      key={opt.value}
                      value={`${opt.value} ${opt.label}`}
                      onSelect={() => {
                        if (!opt.disabled) toggle(opt.value);
                      }}
                      className={cn(
                        "flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm",
                        "text-gray-900 dark:text-gray-100",
                        "hover:bg-gray-100 dark:hover:bg-gray-700",
                        "aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700",
                        opt.disabled && "opacity-50 pointer-events-none"
                      )}
                    >
                      <span>{opt.label}</span>
                      <Check
                        className={cn(
                          "h-4 w-4 text-gray-600 dark:text-gray-300",
                          checked ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </Cmd.Item>
                  );
                })}
              </Cmd.Group>
            </Cmd.List>
          </Cmd>
        </PopoverContent>
      </Popover>
    </div>
  );
}
